import { formatDistanceToNow } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';

import {
  type DeletionJob,
  type DeletionJobStatus,
  LONG_DELETION_MESSAGE,
  LONG_DELETION_THRESHOLD_MS,
  useDeletionStatus,
} from '@/hooks/integrations/useDeletionStatus';
import type { BadgeColors } from '@/lib/base/badges/badge-types';
import { BadgeWithDot } from '@/lib/base/badges/badges';
import { ProgressBar } from '@/lib/base/progress-indicators/progress-indicators';

interface IntegrationDeletionStatusProps {
  orgId: string;
  integrationEntityId: string;
}

type TerminalStatus = Exclude<DeletionJobStatus, 'active'>;

const statusDisplayMap: Record<
  TerminalStatus,
  { text: string; color: BadgeColors }
> = {
  completed: { text: 'Completed', color: 'success' },
  failed: { text: 'Failed', color: 'error' },
  cancelled: { text: 'Cancelled', color: 'gray' },
};

function findDisplayJob(jobs: DeletionJob[]): DeletionJob | null {
  if (jobs.length === 0) {
    return null;
  }

  const activeJobs = jobs.filter(j => j.status === 'active');

  if (activeJobs.length > 0) {
    let maxEtaJob: DeletionJob | null = null;
    let maxEta: string | null = null;

    for (const job of activeJobs) {
      const eta = job.progress.estimated_completion_at;
      if (eta && (!maxEta || eta > maxEta)) {
        maxEta = eta;
        maxEtaJob = job;
      }
    }

    return maxEtaJob ?? activeJobs[0];
  }

  // No active jobs — pick most recently terminal by completed_at || started_at
  return jobs.reduce((latest, job) => {
    const latestTime = latest.completed_at || latest.started_at || '';
    const jobTime = job.completed_at || job.started_at || '';
    return jobTime > latestTime ? job : latest;
  });
}

export default function IntegrationDeletionStatus({
  orgId,
  integrationEntityId,
}: IntegrationDeletionStatusProps) {
  const [, setTick] = useState(0);

  const { jobs, isLoading, error } = useDeletionStatus({
    orgId,
    integrationEntityId,
  });

  const displayJob = useMemo(() => findDisplayJob(jobs), [jobs]);

  const shouldTick =
    displayJob?.status === 'active' &&
    !!displayJob.progress.estimated_completion_at;

  useEffect(() => {
    if (!shouldTick) {
      return;
    }
    const intervalId = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(intervalId);
  }, [shouldTick]);

  if (isLoading) {
    return (
      <div
        className='py-4'
        role='progressbar'
        aria-label='Loading deletion status'
      >
        <div className='w-full h-2 bg-quaternary rounded-md animate-pulse' />
      </div>
    );
  }

  if (error && jobs.length === 0) {
    return (
      <div className='py-2 px-3 bg-utility-error-50 rounded-lg border border-utility-error-200'>
        <p className='text-sm text-utility-error-700'>{error}</p>
      </div>
    );
  }

  if (!displayJob) {
    return null;
  }

  const percent =
    displayJob.status === 'completed'
      ? 100
      : displayJob.progress.percent_complete;

  let etaText: string | null = null;
  let statusDisplay: { text: string; color: BadgeColors } | null = null;

  if (displayJob.status === 'active') {
    if (displayJob.progress.estimated_completion_at) {
      const msRemaining =
        new Date(displayJob.progress.estimated_completion_at).getTime() -
        Date.now();
      if (msRemaining > LONG_DELETION_THRESHOLD_MS) {
        etaText = LONG_DELETION_MESSAGE;
      } else if (msRemaining > 0) {
        etaText = `${formatDistanceToNow(
          new Date(displayJob.progress.estimated_completion_at)
        )} remaining`;
      } else {
        etaText = 'Almost done…';
      }
    } else if (displayJob.progress.items_processed === 0) {
      etaText = 'Starting deletion…';
    }
  } else {
    statusDisplay = statusDisplayMap[displayJob.status];
  }

  return (
    <div className='space-y-2'>
      {displayJob.status === 'active' &&
        displayJob.items_estimated !== null && (
          <p className='text-xs text-tertiary'>
            {displayJob.items_processed.toLocaleString()} of ~
            {displayJob.items_estimated.toLocaleString()}
          </p>
        )}
      <div className='flex items-center gap-2 py-1'>
        <div className='flex-1'>
          <ProgressBar value={percent} min={0} max={100} />
        </div>
        <div className='shrink-0 flex justify-end'>
          {etaText ? (
            <span className='text-xs text-tertiary'>{etaText}</span>
          ) : (
            statusDisplay && (
              <BadgeWithDot type='color' size='sm' color={statusDisplay.color}>
                {statusDisplay.text}
              </BadgeWithDot>
            )
          )}
        </div>
      </div>
    </div>
  );
}
